import { shallowEqual, useSelector } from 'react-redux'
import { useModal, useModalExtra } from '../../../_components/Modal'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { masterClassification, titleName } from '../../../_constants/Code'
import Forbidden from '../../../_components/Forbidden'
import { createBucketName } from '../../../common/utils/StringUtils'
import { useToast } from '../../../_components/Toast'
import { Link } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import CustomTooltip from '../../../_components/CustomTooltip'
import { DataBridge } from '../../common/DataBridge/DataBridge'
import { UserClassificationCategory } from './components/UserClassificationCategory'
import { UserClassificationType } from './components/UserClassificationType'
import {
  UserClassificationInfoProvider,
  useUserClassificationDispatch,
  useUserClassificationSelector,
  useUserClassificationStore
} from './extra/store'
import RelatedDashboard from '../parts/RelatedDashboard'
import LoadingMessage from '../../../_components/LoadingMessage'
import PageLoading from '../../../_components/PageLoading'
import { openMasterConfirmUpdateModal, useConfirmationModal } from '../../../_components/Modal/useConfirmationModal'
import { SpinnerButton } from '../../common/CustomButton'
import { UserClassificationInfoRecord, UserClassificationInfoRecordList } from './extra/types'
import { formatDateCustom, parseDateCustom } from '../../../common/utils/DateUtils'
import { useModifyClassList } from '../../../common/utils/CSSUtils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchUserClassificationRecords,
  fetchUserClassificationTypes,
  updateUserClassificationRecords,
  fetchMaster
} from './extra/services'
import { nanoid } from 'nanoid'
import {
  padString,
  extractChangedRecords,
  createDisplayOrderMap,
  convertRecordToApiFormat,
  afterStateUpdate,
  normalizeValue,
  isInvalidDate,
  parseImportDate,
  refreshClassificationErrors
} from './extra/helpers'
import { errorLog } from '../../../common/logger/Logger'
import { getMessage } from '../../../common/utils/MessageUtils'
import {
  checkIfValueIsUnique,
  isDateRangeOverlapping,
  findRankInconsistentClassifications,
  RANK_CONSISTENCY_ERROR_MESSAGE
} from './extra/validation'

/** インポート時のzod英語エラーメッセージ（Invalid input等）のフィールド別日本語対応表（rank: #3228系、日付: #3230） */
const IMPORT_DATE_FORMAT_ERROR_MESSAGE =
  '取り込めない日付形式です（YYYY/MM/DD または YYYY-MM-DD 形式で入力してください）'
const IMPORT_MESSAGE_REMAP = {
  rank: '数値のみ設定可能です',
  validStartDate: IMPORT_DATE_FORMAT_ERROR_MESSAGE,
  validEndDate: IMPORT_DATE_FORMAT_ERROR_MESSAGE
}

/**
 * @function mapCsv CSV/Excelデータを区分項目レコードに変換（個別レコードごとにバリデーション）
 *   日付はCSV/ExcelともYYYY/MM/DD・YYYY-MM-DDの2形式（0埋めなし可）を受け付ける（parseImportDate参照）
 * @param {Record<string, unknown>[]} records - インポートされたレコード配列
 * @param {string} type - ファイルタイプ（'excel' または 'csv'）
 * @returns {{ mappedRecords: Array; validationErrors: Array }} 変換されたレコードとバリデーションエラー情報
 */
const mapCsv = async (records, type) => {
  const parse = (e) => parseImportDate(e)

  const mappedRecords = []
  const validationErrors = []

  // まず、すべてのレコードをパース（スキーマバリデーション）
  records.forEach((record, index) => {
    // 事前バリデーション: 値が空文字列またはundefinedの場合はエラー
    // Excelの数値セルはnumber型で届くため文字列へ正規化する（数値0は'0'として必須判定を通過）
    const value = normalizeValue(record['値'])
    let preValidationErrors = []

    if (value.trim() === '') {
      preValidationErrors.push({
        field: 'value',
        message: '必須です'
      })
    }

    // 事前バリデーション: ランクが数値に変換できない場合はエラー
    const rankValue = record['ランク']
    if (rankValue != null && rankValue !== '') {
      const rankNumber = Number(rankValue)
      if (isNaN(rankNumber)) {
        preValidationErrors.push({
          field: 'rank',
          message: '数値のみ設定可能です'
        })
      }
    }

    try {
      // 事前バリデーションエラーがある場合はエラーとして扱う
      if (preValidationErrors.length > 0) {
        throw new Error('事前バリデーションエラー')
      }

      const parsed = UserClassificationInfoRecord.parse({
        key: nanoid(),
        code: normalizeValue(record['コード']),
        value: value,
        validStartDate: parse(record['有効開始日']),
        validEndDate: parse(record['有効終了日']),
        memo: normalizeValue(record['メモ']),
        displayOrder: record?.['表示順'] ?? padString(index),
        rank: record['ランク'] != null && record['ランク'] !== '' ? Number(record['ランク']) : null
      })

      mappedRecords.push(parsed)
    } catch (error) {
      // エラー情報を抽出
      const fieldErrors = {}

      // 事前バリデーションエラーを追加
      if (preValidationErrors.length > 0) {
        preValidationErrors.forEach((preError) => {
          if (!fieldErrors[preError.field]) {
            fieldErrors[preError.field] = []
          }
          fieldErrors[preError.field].push(preError.message)
        })
      }

      // zod/miniのエラー構造を確認
      // error.issues または error.errors の可能性がある
      const issues = error.issues || error.errors || []

      if (Array.isArray(issues) && issues.length > 0) {
        issues.forEach((issue) => {
          // pathは配列または文字列の可能性がある
          const path = issue.path || issue.field || []
          const field = Array.isArray(path) ? path[0] : path
          const fieldName = field || 'unknown'

          if (!fieldErrors[fieldName]) {
            fieldErrors[fieldName] = []
          }

          let message = issue.message || issue.msg || 'バリデーションエラー'
          if (IMPORT_MESSAGE_REMAP[fieldName] && /invalid/i.test(message)) {
            message = IMPORT_MESSAGE_REMAP[fieldName]
          }
          fieldErrors[fieldName].push(message)
        })
      } else if (preValidationErrors.length === 0) {
        // 事前バリデーションエラーがない場合のみ、エラーオブジェクトから直接情報を取得
        const errorMessage = error.message || String(error)
        if (errorMessage !== '事前バリデーションエラー') {
          fieldErrors.unknown = [errorMessage]
        }
      }

      // エラー情報を記録
      Object.keys(fieldErrors).forEach((field) => {
        fieldErrors[field].forEach((message) => {
          validationErrors.push({
            recordIndex: index,
            field,
            message
          })
        })
      })

      // 可能な限りデータを保持（エラーフィールドはnullや空文字で）
      const recordKey = nanoid()
      const parsedValidStartDate = parse(record['有効開始日'])
      const parsedValidEndDate = parse(record['有効終了日'])
      // パース失敗（Invalid Date）の日付は一覧セルが空欄になり「無期限で取り込まれた」と
      // 誤解される。取り込み元の生値を保持し、セル側で表示する（バグ #3230）
      mappedRecords.push({
        key: recordKey,
        code: normalizeValue(record['コード']),
        value: normalizeValue(record['値']),
        validStartDate: parsedValidStartDate,
        validEndDate: parsedValidEndDate,
        memo: normalizeValue(record['メモ']),
        displayOrder: record?.['表示順'] ?? padString(index),
        rank: record['ランク'] != null && record['ランク'] !== '' ? Number(record['ランク']) : null,
        status: null,
        type: undefined,
        isCreated: true,
        _hasValidationError: true,
        _validationErrors: fieldErrors,
        _rawValidStartDate: isInvalidDate(parsedValidStartDate) ? normalizeValue(record['有効開始日']) : undefined,
        _rawValidEndDate: isInvalidDate(parsedValidEndDate) ? normalizeValue(record['有効終了日']) : undefined
      })
    }
  })

  // 重複チェック: インポートするレコード同士の重複をチェック
  mappedRecords.forEach((record, index) => {
    const fieldErrors = {}

    // 同じインポートデータ内の他のレコードと重複チェック
    mappedRecords.forEach((otherRecord, otherIndex) => {
      // 自分自身はスキップ
      if (index === otherIndex) {
        return
      }

      // 比較用の正規化関数
      const normalize = (value) => {
        if (value == null) return ''
        return String(value).trim()
      }

      const normalizedValue = normalize(record.value)
      const normalizedCode = normalize(record.code)
      const normalizedOtherValue = normalize(otherRecord.value)
      const normalizedOtherCode = normalize(otherRecord.code)

      // 値の重複チェック: 値が同じで、かつ有効期間が重複している場合
      if (normalizedValue === normalizedOtherValue && normalizedValue !== '') {
        const hasOverlap = isDateRangeOverlapping(
          record.validStartDate,
          record.validEndDate,
          otherRecord.validStartDate,
          otherRecord.validEndDate
        )
        if (hasOverlap) {
          if (!fieldErrors.value) {
            fieldErrors.value = []
          }
          if (!fieldErrors.value.includes('同一の値で有効期間が重複しているものがあります')) {
            fieldErrors.value.push('同一の値で有効期間が重複しているものがあります')
          }
        }
      }

      // コードの重複チェック: コードが同じで、かつ有効期間が重複している場合
      if (normalizedCode !== '' && normalizedCode === normalizedOtherCode) {
        const hasOverlap = isDateRangeOverlapping(
          record.validStartDate,
          record.validEndDate,
          otherRecord.validStartDate,
          otherRecord.validEndDate
        )
        if (hasOverlap) {
          if (!fieldErrors.code) {
            fieldErrors.code = []
          }
          if (!fieldErrors.code.includes('同一のコードで有効期間が重複しているものがあります')) {
            fieldErrors.code.push('同一のコードで有効期間が重複しているものがあります')
          }
        }
      }
    })

    // 重複エラーがある場合は、エラー情報を追加
    if (Object.keys(fieldErrors).length > 0) {
      Object.keys(fieldErrors).forEach((field) => {
        fieldErrors[field].forEach((message) => {
          validationErrors.push({
            recordIndex: index,
            field,
            message
          })
        })
      })

      // レコードにエラー情報を追加
      if (!record._hasValidationError) {
        record._hasValidationError = true
        record._validationErrors = {}
      }
      Object.assign(record._validationErrors, fieldErrors)
    }
  })

  return {
    mappedRecords,
    validationErrors
  }
}

const UserClassificationInfoContent = (props) => {
  const [master, setMaster] = useState({})

  // 初期表示（区分タイプ一覧の取得）中のローディング表示制御
  const [loading, setLoading] = useState(true)
  // 区分項目の取得（初期表示の2段階目・区分切替時）中のローディング表示制御
  const [isRecordsLoading, setIsRecordsLoading] = useState(false)

  const title = '区分マスター設定（ユーザー設定）'
  const server = useUserClassificationSelector((s) => s.errors.server)
  const selectedClassificationType = useUserClassificationSelector(
    (s) => s.userClassificationTypes.find((e) => e.code === s.selectedUserClassificationType),
    shallowEqual
  )

  const dispatch = useUserClassificationDispatch()
  const store = useUserClassificationStore()
  const changeHistory = useUserClassificationSelector((s) => s.changeHistory || [])
  const pendingUpdates = useUserClassificationSelector((s) => s.pendingUpdates || {})
  const errorsByClassification = useUserClassificationSelector((s) => s.errors?.byClassification || {})

  // DataBridgeのリセット用カウンター
  const [dataBridgeResetKey, setDataBridgeResetKey] = useState(0)
  /** DataBridge 共通部は触らず、当画面だけ省略＋ツールチップ用に保持するインポートファイル名 */
  const [dataBridgeImportFileLabel, setDataBridgeImportFileLabel] = useState('')
  const selectedUserClassificationTypeCode = useUserClassificationSelector((s) => s.selectedUserClassificationType)

  useEffect(() => {
    setDataBridgeImportFileLabel('')
  }, [selectedUserClassificationTypeCode, dataBridgeResetKey])

  useEffect(() => {
    document.title = props.pageTitle
  }, [props.pageTitle])

  // モーダルの幅を広げるためのフック
  const { promisify: promisifyModalWidth } = useModifyClassList('.modal-window', ['w-[900px]'])

  // キャンセル時の確認ダイアログ用フック
  const openConfirmationModal = useConfirmationModal()

  const init = async () => {
    try {
      // 1段階目: 区分タイプ一覧・マスター情報の取得
      const pTypes = fetchUserClassificationTypes(store)
      const pMaster = fetchMaster(props.corporationNumber)

      const [typesData, masterData] = await Promise.all([pTypes, pMaster])
      const { types, changeHistory } = typesData

      setMaster(masterData)

      dispatch('INIT_STATE', {
        records: [],
        types,
        changeHistory
      })

      // 2段階目: 最初の区分タイプの区分項目を取得（この間は区分項目パネルにローディングを表示）
      if (types.length > 0) {
        const firstType = types[0]
        try {
          setIsRecordsLoading(true)
          const records = await fetchUserClassificationRecords(
            store,
            firstType.code,
            firstType.standardFormatColumnName
          )
          dispatch('SET_CLASSIFICATION_RECORDS', {
            classificationType: firstType.code,
            records
          })
          // 初期状態を保存（変換後のレコードを取得）
          afterStateUpdate(() => {
            const state = store.getState()
            const convertedRecords = state.userClassificationRecords[firstType.code] || []
            dispatch('SAVE_INITIAL_RECORDS', {
              classificationType: firstType.code,
              records: convertedRecords
            })
          })
        } catch (error) {
          errorLog('E000001', props.userId || 'unknown', 'userClassificationInfo', 'init', error)
          dispatch('SET_STATE', {
            path: 'errors.server',
            value: [getMessage('E010001', '区分項目')]
          })
        } finally {
          setIsRecordsLoading(false)
        }
      }
    } catch (error) {
      errorLog('E000001', props.userId || 'unknown', 'userClassificationInfo', 'init', error)
      dispatch('SET_STATE', {
        path: 'errors.server',
        value: [getMessage('E010001', '区分項目')]
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
  }, [])

  /**
   * @function handleSelectClassificationType 区分タイプ選択時の処理
   * @param {Object} selectedType - 選択された区分タイプ
   */
  const handleSelectClassificationType = useCallback(
    async (selectedType) => {
      // 区分タイプを選択
      dispatch('SET_STATE', {
        path: 'selectedUserClassificationType',
        value: selectedType.code
      })

      // 変更情報を保持しているかチェック
      const { pendingUpdates, updateStatus, initialClassificationRecords } = store.getState()
      const hasPendingChanges = pendingUpdates[selectedType.code] || updateStatus[selectedType.code] === 'pending'

      // 変更情報を保持している場合は、取得IFを呼び出さずに保持している情報を表示
      if (hasPendingChanges) {
        return
      }

      // 変更情報がなく、初期状態が保存されている場合は取得IFを呼び出さずに初期状態を表示
      if (initialClassificationRecords?.[selectedType.code]) {
        const initialRecords = initialClassificationRecords[selectedType.code]
        // 初期状態を深いコピーで取得し、日付文字列をDateオブジェクトに変換
        const resetRecords = JSON.parse(JSON.stringify(initialRecords)).map((record) => {
          const parseDate = (dateStr) => {
            if (!dateStr || dateStr === '') return null
            return parseDateCustom(dateStr, 'yyyy/MM/dd')
          }
          return {
            ...record,
            validStartDate: parseDate(record.validStartDate),
            validEndDate: parseDate(record.validEndDate)
          }
        })
        // 区分項目を初期状態で設定（SET_STATEで直接更新）
        dispatch('SET_STATE', {
          path: `userClassificationRecords.${selectedType.code}`,
          value: resetRecords
        })

        // 区分選択時に、初期状態の件数でitemCountを更新
        dispatch('UPDATE_CLASSIFICATION_TYPE_COUNT', {
          classificationType: selectedType.code,
          itemCount: resetRecords.length
        })
        return
      }

      // 変更情報がなく、初期状態も保存されていない場合のみ、IF02070を呼び出して区分項目を取得
      try {
        setIsRecordsLoading(true)
        const standardFormatColumnName = selectedType.standardFormatColumnName || selectedType.label
        const records = await fetchUserClassificationRecords(store, selectedType.code, standardFormatColumnName)

        // 区分項目を設定
        dispatch('SET_CLASSIFICATION_RECORDS', {
          classificationType: selectedType.code,
          records
        })

        // 初期状態を保存（変換後のレコードを取得）
        afterStateUpdate(() => {
          const state = store.getState()
          const convertedRecords = state.userClassificationRecords[selectedType.code] || []
          dispatch('SAVE_INITIAL_RECORDS', {
            classificationType: selectedType.code,
            records: convertedRecords
          })

          // 区分選択時に、IF02070から取得した実際の件数でitemCountを更新（未保存の変更を破棄）
          dispatch('UPDATE_CLASSIFICATION_TYPE_COUNT', {
            classificationType: selectedType.code,
            itemCount: convertedRecords.length
          })
        })
      } catch (error) {
        errorLog(
          'E000001',
          props.userId || 'unknown',
          'userClassificationInfo',
          'handleSelectClassificationType',
          error
        )
        dispatch('SET_STATE', {
          path: 'errors.server',
          value: [getMessage('E010001', '区分項目')]
        })
      } finally {
        setIsRecordsLoading(false)
      }
    },
    [dispatch, store]
  )

  /**
   * @function prepareUpdateData 更新IFに渡すデータを準備
   * @param {string} classificationCode - 区分コード
   * @returns {Object} 更新データ
   */
  const prepareUpdateData = useCallback(
    (classificationCode) => {
      const {
        userClassificationTypes,
        userClassificationRecords: classificationRecords,
        initialClassificationRecords,
        importedFlags
      } = store.getState()

      const records = classificationRecords[classificationCode] || []
      const initialRecords = initialClassificationRecords?.[classificationCode] || []
      const initialRecordsMap = new Map(initialRecords.map((r) => [r.key, r]))

      // 変更されたレコードを抽出
      const changedRecords = extractChangedRecords(records, initialRecordsMap)

      // インポートフラグを取得
      const importedFlg = importedFlags?.[classificationCode] || false

      // 区分タイプ情報を取得
      const selectedType = userClassificationTypes.find((t) => t.code === classificationCode)

      // 変更がない場合は空のtable_dataを返す
      if (changedRecords.length === 0) {
        return {
          data_classification_type: `${classificationCode}#classification`,
          data_classification_name: selectedType?.label,
          standard_format_column_name: selectedType?.standardFormatColumnName || '',
          table_data: [],
          imported_flg: importedFlg
        }
      }

      // 変更されたレコードのvalueのセットを作成（重複チェック用）
      const changedRecordKeys = new Set(changedRecords.map((r) => r.key))
      const changedValues = new Set(changedRecords.map((r) => normalizeValue(r.value)))

      // 同じvalueを持つレコードを追加（削除されていない、かつ変更されていない）
      const recordsWithSameValue = records
        .filter((record) => {
          // 削除されていない
          if (record.isDeleted) return false
          // 既に変更されているレコードは除外（重複チェック）
          if (changedRecordKeys.has(record.key)) return false
          // valueが変更されたレコードのvalueと一致する
          return changedValues.has(normalizeValue(record.value))
        })
        .map((record) => ({
          ...record,
          isSkipOnly: true
        }))

      // 変更されたレコードと追加レコードを結合
      const allRecordsToUpdate = [...changedRecords, ...recordsWithSameValue]

      // 表示順マップを作成
      const allRecords = classificationRecords[classificationCode] || []
      const displayOrderMap = createDisplayOrderMap(allRecords)

      // APIリクエスト形式に変換
      const tableData = allRecordsToUpdate.map((record) => convertRecordToApiFormat(record, displayOrderMap))

      return {
        data_classification_type: `${classificationCode}#classification`,
        data_classification_name: selectedType?.label,
        standard_format_column_name: selectedType?.standardFormatColumnName || '',
        table_data: tableData,
        imported_flg: importedFlg
      }
    },
    [store]
  )

  /**
   * @function markClassificationAsPending 区分を未反映状態にマーク
   * @param {string} classificationCode - 区分コード
   */
  const markClassificationAsPending = useCallback(
    (classificationCode) => {
      const state = store.getState()
      const { initialClassificationRecords, userClassificationRecords } = state

      // initialClassificationRecordsが存在しない場合は、現在の状態を初期状態として保存
      if (!initialClassificationRecords || !initialClassificationRecords[classificationCode]) {
        const currentRecords = userClassificationRecords[classificationCode] || []
        dispatch('SAVE_INITIAL_RECORDS', {
          classificationType: classificationCode,
          records: JSON.parse(JSON.stringify(currentRecords))
        })
      }

      // 更新データを準備
      const updateData = prepareUpdateData(classificationCode)

      // 変更がある場合のみ未反映状態にマーク
      if (updateData.table_data && updateData.table_data.length > 0) {
        // 未反映状態にマーク
        dispatch('MARK_CLASSIFICATION_AS_PENDING', { classificationCode })

        // 更新データを保存
        dispatch('SAVE_PENDING_UPDATE', {
          classificationCode,
          updateData
        })
      } else {
        // 変更がない場合は未反映状態を解除
        const { pendingUpdates } = store.getState()
        if (pendingUpdates[classificationCode]) {
          const newPendingUpdates = { ...pendingUpdates }
          delete newPendingUpdates[classificationCode]
          dispatch('SET_STATE', {
            path: 'pendingUpdates',
            value: newPendingUpdates
          })
        }
        dispatch('UPDATE_CLASSIFICATION_STATUS', {
          classificationCode,
          status: null
        })
      }
    },
    [dispatch, prepareUpdateData, store]
  )

  /**
   * @function handleImport
   * @param {Record<string, unknown>} records
   * @param {{ name: string; type: string }} fileMeta
   */
  const handleImport = useCallback(
    async (records, fileMeta) => {
      try {
        // 現在の区分のエラー情報をクリア（区分ごとに保持するため、該当区分のみクリア）
        const { errors, selectedUserClassificationType } = store.getState()
        if (errors?.importWarnings?.[selectedUserClassificationType]) {
          const newImportWarnings = { ...errors.importWarnings }
          delete newImportWarnings[selectedUserClassificationType]
          dispatch('SET_STATE', {
            path: 'errors.importWarnings',
            value: newImportWarnings
          })
        }
        if (errors?.importValidationErrors?.[selectedUserClassificationType]) {
          const newImportValidationErrors = { ...errors.importValidationErrors }
          delete newImportValidationErrors[selectedUserClassificationType]
          dispatch('SET_STATE', {
            path: 'errors.importValidationErrors',
            value: newImportValidationErrors
          })
        }
        // recordValidationErrorsは区分ごとに保持されているため、該当区分のレコードのエラーのみ削除
        if (errors?.recordValidationErrors) {
          const newRecordValidationErrors = { ...errors.recordValidationErrors }
          const records = store.getState().userClassificationRecords?.[selectedUserClassificationType] || []
          records.forEach((record) => {
            if (newRecordValidationErrors[record.key]) {
              delete newRecordValidationErrors[record.key]
            }
          })
          dispatch('SET_STATE', {
            path: 'errors.recordValidationErrors',
            value: newRecordValidationErrors
          })
        }

        // 現在の区分項目一覧から、状態が「使用中」のレコードの値を取得
        const {
          userClassificationRecords: classificationRecords,
          selectedUserClassificationType: selectedClassificationType
        } = store.getState()
        const currentRecords = classificationRecords[selectedClassificationType] || []
        const usedRecords = currentRecords.filter((record) => record.status === '使用中' && !record.isDeleted)
        const usedValues = usedRecords.map((record) => record.value)

        // インポートするデータの値のリストを作成
        // Excel の数値セルは number 型で届くため、既存値（文字列）との比較用に文字列へ揃える
        const importValues = records
          .map((record) => (record['値'] == null ? '' : String(record['値'])))
          .filter((value) => value !== '')

        // 使用中のレコードの値が全てインポートデータに含まれているかチェック
        const missingValues = usedValues.filter((value) => !importValues.includes(value))

        let hasBusinessLogicWarning = false
        if (missingValues.length > 0) {
          // ビジネスロジックチェックエラー: 警告メッセージを設定し、表を更新する（区分ごとに保持）
          const missingValuesText = missingValues.join('、')
          const { errors } = store.getState()
          const newImportWarnings = { ...(errors?.importWarnings || {}) }
          newImportWarnings[selectedClassificationType] = [`使用中の区分項目がインポートデータに含まれていません`]
          dispatch('SET_STATE', {
            path: 'errors.importWarnings',
            value: newImportWarnings
          })
          hasBusinessLogicWarning = true
        }

        // データ変換とバリデーション
        const { mappedRecords, validationErrors } = await mapCsv(records, fileMeta.type)

        // デバッグ用: バリデーションエラー情報をログ出力
        if (validationErrors.length > 0) {
          console.log('バリデーションエラー検出:', validationErrors)
        }

        // バリデーションエラー情報を設定（区分ごとに保持）
        if (validationErrors.length > 0) {
          const { errors } = store.getState()
          const newImportValidationErrors = { ...(errors?.importValidationErrors || {}) }
          newImportValidationErrors[selectedClassificationType] = validationErrors
          dispatch('SET_STATE', {
            path: 'errors.importValidationErrors',
            value: newImportValidationErrors
          })

          // レコードごとのエラー情報を構築（区分ごとに保持）
          const { errors: currentErrors } = store.getState()
          const newRecordValidationErrors = { ...(currentErrors?.recordValidationErrors || {}) }
          mappedRecords.forEach((record) => {
            if (record._hasValidationError && record._validationErrors) {
              newRecordValidationErrors[record.key] = record._validationErrors
            }
          })

          dispatch('SET_STATE', {
            path: 'errors.recordValidationErrors',
            value: newRecordValidationErrors
          })
        }

        // 表を更新（エラーレコードも含む）
        dispatch('BATCH_ADD_CLASSIFICATION', mappedRecords)

        // インポートフラグを設定
        dispatch('MARK_IMPORTED', { classificationCode: selectedClassificationType })

        // 未反映状態にマーク（Reduxの状態更新を待つ）
        afterStateUpdate(() => {
          // 区分単位エラー（ランク整合性・サーバー由来）をインポート後の状態で再判定して除去（バグ #3293）
          refreshClassificationErrors(store, dispatch, selectedClassificationType)
          markClassificationAsPending(selectedClassificationType)
        })

        // ビジネスロジックチェックエラーまたはバリデーションエラーがある場合でも成功として扱う
        setDataBridgeImportFileLabel(fileMeta?.name || '')
        return true
      } catch (error) {
        // ファイル取り込み例外は英語を表示せず E011023 を表示（バグ #3228）
        console.warn(error)
        dispatch('SET_STATE', {
          path: 'errors.server',
          value: [getMessage('E011023')]
        })
        return false
      }
    },
    [dispatch, store, markClassificationAsPending]
  )

  /** @function getRecords */
  const getRecords = useCallback(() => {
    const format = (date) => formatDateCustom(date, 'yyyy-MM-dd')
    const {
      userClassificationRecords: classificationRecords,
      selectedUserClassificationType: selectedClassificationType
    } = store.getState()

    // 削除されていない項目のみをエクスポート
    const activeRecords = (classificationRecords[selectedClassificationType] || []).filter((e) => !e.isDeleted)

    return activeRecords.map((e) => ({
      コード: e.code,
      値: e.value,
      有効開始日: format(e.validStartDate),
      有効終了日: format(e.validEndDate),
      ランク: e.rank ?? '',
      状態: e.status || '未使用',
      メモ: e.memo || ''
    }))
  }, [store])

  /**
   * @function validateRank 登録対象（未反映）の全区分についてランクの整合性をチェック 画面上で選択中の区分だけでなく、登録対象となる未反映の全区分を検証する。
   * @returns {string[]} ランク不整合の区分コード配列（整合していれば空配列）
   */
  const validateRank = useCallback(() => {
    const {
      userClassificationRecords: classificationRecords,
      pendingUpdates,
      userClassificationTypes
    } = store.getState()

    // 登録対象となる未反映の全区分を検証対象とする
    const pendingCodes = new Set(Object.keys(pendingUpdates || {}))

    // 自動切り替え時に「先頭区分」が左側一覧の表示順と一致するよう、表示順で検証する
    const targetClassificationCodes = (userClassificationTypes || [])
      .map((t) => t.code)
      .filter((code) => pendingCodes.has(code))

    return findRankInconsistentClassifications(targetClassificationCodes, classificationRecords)
  }, [store])

  /** @function openChangeHistoryModal 変更履歴モーダルを表示 */
  const openChangeHistoryModal = useCallback(async () => {
    try {
      const ChangeHistoryContent = () => {
        if (changeHistory.length === 0) {
          return (
            <div className="text-center py-8 text-gray-500">
              <p>変更履歴はありません</p>
            </div>
          )
        }

        return (
          <div className="setting-table is-segment pt-4">
            <div className="overflow-y-scroll max-h-[60vh]">
              <table className="setting-table__table table-fixed w-full form-control mb-5">
                <colgroup>
                  <col style={{ width: '250px' }} />
                  <col style={{ width: 'auto', minWidth: '300px' }} />
                  <col style={{ width: '200px' }} />
                </colgroup>
                <thead className="sticky top-0 !z-[39] bg-white">
                  <tr>
                    <th className="base-table-header bg-white" style={{ width: '250px' }}>
                      更新日時
                    </th>
                    <th className="base-table-header bg-white" style={{ width: 'auto', minWidth: '300px' }}>
                      区分名
                    </th>
                    <th className="base-table-header bg-white" style={{ width: '200px' }}>
                      更新者
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {changeHistory.map((item, index) => (
                    <tr key={index}>
                      <td style={{ width: '250px' }}>{item.update_date_time || ''}</td>
                      <td style={{ width: 'auto', minWidth: '300px' }}>{item.classification_name || ''}</td>
                      <td style={{ width: '200px' }}>{item.update_user_name || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }

      await promisifyModalWidth((res) => {
        props.showModal({
          title: (
            <div className="flex items-center">
              <AssignmentIcon className="w-5 h-5 mr-2 text-gray-600" />
              <span>変更履歴</span>
            </div>
          ),
          isWide: false,
          isFooter: false,
          onClose: res,
          titleAlign: 'left',
          closeButtonStyle: 'icon',
          body: <ChangeHistoryContent />
        })
      })
    } catch (error) {
      errorLog('E000001', props.userId || 'unknown', 'userClassificationInfo', 'openChangeHistoryModal', error)
    }
  }, [props, changeHistory, promisifyModalWidth])

  /**
   * @function canRegister 登録ボタンを活性化できるかチェック
   * @returns {boolean} 未反映の区分が1つ以上あり、かつエラーメッセージ（警告は除く）がない場合のみtrue
   */
  const canRegister = useMemo(() => {
    const state = store.getState()
    const errors = state.errors || {}

    // 未反映の区分が無い場合は非活性
    if (Object.keys(pendingUpdates).length === 0) {
      return false
    }

    // 区分項目一覧の下にエラーメッセージ（警告は除く）が表示されている区分が1つ以上ある場合は非活性
    // 区分ごとのエラー（errorsByClassification）をチェック
    const hasErrorsByClassification = Object.values(errorsByClassification).some(
      (errors) => errors && errors.length > 0
    )
    if (hasErrorsByClassification) {
      return false
    }

    // インポートバリデーションエラー（importValidationErrors）をチェック（警告は除く）
    if (errors.importValidationErrors) {
      const hasImportValidationErrors = Object.values(errors.importValidationErrors).some(
        (validationErrors) => validationErrors && validationErrors.length > 0
      )
      if (hasImportValidationErrors) {
        return false
      }
    }

    // 未反映の区分が1つ以上あり、かつエラーメッセージ（警告は除く）がない場合のみtrue
    return true
  }, [pendingUpdates, errorsByClassification, store])

  const openConfirmUpdateModal = () => {
    return openMasterConfirmUpdateModal({
      onClick: async () => {
        try {
          // 登録対象（未反映）の全区分についてランクの整合性チェック
          const rankErrorCodes = validateRank()
          if (rankErrorCodes.length > 0) {
            props.showToast(RANK_CONSISTENCY_ERROR_MESSAGE)
            // 不整合の各区分ごとにエラーメッセージを追加
            const { errors, selectedUserClassificationType } = store.getState()
            const newErrorsByClassification = { ...(errors?.byClassification || {}) }
            rankErrorCodes.forEach((classificationCode) => {
              if (!newErrorsByClassification[classificationCode]) {
                newErrorsByClassification[classificationCode] = []
              }
              // 既に同じエラーメッセージが含まれていない場合のみ追加
              if (!newErrorsByClassification[classificationCode].includes(RANK_CONSISTENCY_ERROR_MESSAGE)) {
                newErrorsByClassification[classificationCode].push(RANK_CONSISTENCY_ERROR_MESSAGE)
              }
            })
            dispatch('SET_STATE', {
              path: 'errors.byClassification',
              value: newErrorsByClassification
            })

            // 選択中の区分が不整合でない場合は、不整合の先頭区分へ自動的に切り替えてエラーを可視化する。
            // 不整合の区分は必ず未反映（pending）のため、データを保持したまま選択のみ切り替える。
            const firstErrorCode = rankErrorCodes[0]
            if (!rankErrorCodes.includes(selectedUserClassificationType)) {
              dispatch('SET_STATE', {
                path: 'selectedUserClassificationType',
                value: firstErrorCode
              })
            }
            // 切り替え後にエラー箇所が見えるようスクロールする。
            // ページ最上部までは戻さず、区分設定セクション（ヘッダ＋エラー＋テーブル）の
            // 先頭が画面上部に収まる位置で止める。
            afterStateUpdate(() => {
              const sectionEl = document.querySelector('[data-testid="userClassificationSection"]')
              if (sectionEl) {
                // 右側パネル内部のスクロールも先頭に戻し、エラー行・テーブル先頭を表示する
                const rightPanel = sectionEl.children?.[1]
                if (rightPanel) rightPanel.scrollTop = 0
                sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            })
            return
          }

          // 未反映の区分を取得
          const { pendingUpdates, userClassificationTypes } = store.getState()
          const pendingClassifications = Object.keys(pendingUpdates)

          if (pendingClassifications.length === 0) {
            const errorMessage = '変更された区分項目がありません'
            props.showToast(errorMessage)
            dispatch('SET_STATE', {
              path: 'errors.server',
              value: [errorMessage]
            })
            return
          }

          // 全ての区分情報をまとめて準備
          const allClassificationsData = pendingClassifications
            .map((classificationCode) => {
              const updateData = pendingUpdates[classificationCode]
              // table_dataが空の場合は除外（変更がない区分）
              if (!updateData.table_data || updateData.table_data.length === 0) {
                return null
              }
              return {
                data_classification_type: `${classificationCode}#classification`,
                data_classification_name: updateData.data_classification_name,
                standard_format_column_name: updateData.standard_format_column_name || '',
                table_data: updateData.table_data,
                imported_flg: updateData.imported_flg
              }
            })
            .filter((data) => data !== null)

          if (allClassificationsData.length === 0) {
            const errorMessage = '変更された区分項目がありません'
            props.showToast(errorMessage)
            dispatch('SET_STATE', {
              path: 'errors.server',
              value: [errorMessage]
            })
            return
          }

          // 全ての区分を1回のリクエストで更新
          try {
            await updateUserClassificationRecords(store, allClassificationsData)

            // 成功時: 全ての区分のステータスを更新完了に変更
            pendingClassifications.forEach((classificationCode) => {
              dispatch('UPDATE_CLASSIFICATION_STATUS', {
                classificationCode,
                status: 'success'
              })
            })

            // 成功時にエラーをクリア
            dispatch('SET_STATE', {
              path: 'errors.byClassification',
              value: {}
            })
            dispatch('SET_STATE', {
              path: 'errors.server',
              value: []
            })

            props.navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
          } catch (error) {
            // エラー時: エラーメッセージを表示
            errorLog('E000001', props.userId || 'unknown', 'userClassificationInfo', 'openConfirmUpdateModal', error)
            const errorMessage = error.message || getMessage('E011012')
            const errorMessages = errorMessage.split('\n').filter((msg) => msg.trim())

            // パース済みエラーがある場合は区分ごとに保存
            if (error.parsedErrors) {
              dispatch('SET_STATE', {
                path: 'errors.byClassification',
                value: error.parsedErrors
              })
            }

            // 全体のエラーメッセージは画面上部（#serverErrors）にのみ表示する。
            // トーストは区分ごとのエラー内訳（parsedErrors）を表示できず二重表示となるため出さない。
            dispatch('SET_STATE', {
              path: 'errors.server',
              value: errorMessages.length > 0 ? errorMessages : [errorMessage]
            })
          }
        } catch (error) {
          const state = store.getState()
          errorLog('E000001', props.userId || 'unknown', 'userClassificationInfo', 'openConfirmUpdateModal', error)
          const errorMessage = error.message || getMessage('E011012')
          const errorMessages = errorMessage.split('\n').filter((msg) => msg.trim())

          // パース済みエラーがある場合は区分ごとに保存
          if (error.parsedErrors) {
            dispatch('SET_STATE', {
              path: 'errors.byClassification',
              value: error.parsedErrors
            })
          }

          // 全体のエラーメッセージは画面上部（#serverErrors）にのみ表示する。
          // トーストは区分ごとのエラー内訳（parsedErrors）を表示できず二重表示となるため出さない。
          dispatch('SET_STATE', {
            path: 'errors.server',
            value: errorMessages.length > 0 ? errorMessages : [errorMessage]
          })
        }
      },
      onError: (error) => {
        const state = store.getState()
        errorLog('E000001', state.userId || 'unknown', 'userClassificationInfo', 'openConfirmUpdateModal', error)
        const errorMessage = getMessage('E011012')
        // 全体のエラーメッセージは画面上部（#serverErrors）にのみ表示する（トーストとの二重表示を避ける）。
        dispatch('SET_STATE', {
          path: 'errors.server',
          value: [errorMessage]
        })
      },
      showModal: props.showModal
    })
  }

  /** @function handleCancel キャンセルボタン押下時の処理 未反映の区分がある場合は確認ダイアログを表示し、「破棄して戻る」でマスター管理画面へ戻る */
  const handleCancel = useCallback(() => {
    // 区分一覧の「未反映」バッジ（TypeFilter）と同一の条件（updateStatus が 'pending'）で変更有無を判定する
    const { updateStatus } = store.getState()
    const hasPendingChanges = Object.values(updateStatus || {}).some((status) => status === 'pending')

    if (!hasPendingChanges) {
      props.navigate('/master/masterList')
      return
    }

    openConfirmationModal({
      title: '未反映の変更があります',
      description: (
        <>
          <p>登録されていない区分マスターの変更があります。</p>
          <p>
            マスター管理画面に戻ると、現在設定中の内容は破棄されます。
            <span className="whitespace-nowrap">よろしいですか？</span>
          </p>
        </>
      ),
      confirmName: '破棄して戻る',
      onClick: () => {
        props.navigate('/master/masterList')
      }
    })
  }, [openConfirmationModal, props, store])

  if (loading) {
    return <PageLoading />
  }

  return (
    <>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">{'区分関連マスター'}</h1>
            </header>
            <div className="page-body">
              <div id="serverErrors">
                {server?.map((e, index) => (
                  <p key={index} className="red--text">
                    {e}
                  </p>
                ))}
              </div>
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  {title}
                </h2>
                <div className="my-6 flex justify-between items-start">
                  <div className="flex-shrink min-w-0 pr-4">
                    分析・グラフ用の各種区分を登録してください。コード管理に対応するため、必要に応じてコードも登録できます。
                    <br />
                    アップロード時は汎用データマッピング画面で「マスター変換」を選び、コードを値へ変換してください。対応するため、必要に応じてコードも登録できます。
                  </div>
                  <button
                    type="button"
                    className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                    onClick={() => {
                      openChangeHistoryModal()
                    }}
                  >
                    <AssignmentIcon className="w-5 h-5" />
                    <span>変更履歴</span>
                  </button>
                </div>

                <section
                  data-testid="userClassificationSection"
                  className="flex gap-3 h-[1000px] max-h-[1000px] min-h-0 overflow-hidden"
                >
                  <div className="w-[30%] min-w-[280px] max-w-[300px] flex-shrink-0 h-full min-h-0 border border-gray-300 overflow-y-auto">
                    <UserClassificationType
                      showModal={props.showModal}
                      onSelectClassificationType={handleSelectClassificationType}
                    />
                  </div>

                  <div className="border border-gray-300 flex-1 min-h-0 min-w-0 overflow-auto [scrollbar-gutter:stable]">
                    <div className="sticky top-0 z-[12] flex h-[70px] shrink-0 justify-between bg-white p-3 px-5 shadow-[0_1px_2px_0_rgb(0_0_0_/_0.05),inset_0_-1px_0_0_rgb(229_231_235)]">
                      <h2 className="min-w-0 flex-1 truncate font-medium">{selectedClassificationType?.label ?? ''}</h2>
                      <div className="flex min-w-0 items-center justify-end gap-2">
                        {dataBridgeImportFileLabel ? (
                          <div className="min-w-0 max-w-md overflow-hidden">
                            <CustomTooltip title={dataBridgeImportFileLabel} placement="top">
                              {(ref) => (
                                <div
                                  ref={ref}
                                  data-testid="user-classification-import-file-name"
                                  className="block w-full min-w-0 truncate text-sm text-[#383947]"
                                >
                                  {dataBridgeImportFileLabel}
                                </div>
                              )}
                            </CustomTooltip>
                          </div>
                        ) : null}
                        <div
                          className={
                            dataBridgeImportFileLabel
                              ? 'shrink-0 [&_.content-footer>p:first-of-type]:hidden'
                              : 'shrink-0'
                          }
                        >
                          <DataBridge
                            key={'selectedType' + selectedClassificationType?.code + '_reset' + dataBridgeResetKey}
                            filename={selectedClassificationType?.label || '区分マスター'}
                            getRecords={getRecords}
                            onImport={handleImport}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-300 p-4 pt-0 relative min-h-[70%] ">
                      {isRecordsLoading ? (
                        <LoadingMessage message="データ読み込み中..." minHeightClassName="min-h-[200px]" />
                      ) : (
                        <UserClassificationCategory
                          onMarkClassificationAsPending={markClassificationAsPending}
                          onReset={() => setDataBridgeResetKey((prev) => prev + 1)}
                        />
                      )}
                    </div>
                  </div>
                </section>

                <div className="content-footer my-8">
                  <div className="border-t border-blue1" />
                  {/*  ===== フッターボタン ===== */}
                  <div className="footer_button_area mt-8">
                    <button type="button" className="footer-btn button_back" onClick={handleCancel}>
                      キャンセル
                    </button>
                    <SpinnerButton
                      name="登録"
                      className="footer-btn button_base"
                      onClick={openConfirmUpdateModal}
                      disabled={!canRegister}
                    />
                  </div>
                </div>
              </div>

              {master?.RelatedDashboard && (
                <RelatedDashboard
                  dashboards={[...master?.RelatedDashboard]}
                  authority={props.authority}
                  mode={props.mode}
                />
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

const UserClassificationInfo = () => {
  const showModal = useModal()
  const modalExtra = useModalExtra()
  const navigate = useNavigate()
  const showToast = useToast()

  const state = useSelector((state) => {
    return {
      corporationNumber: state.company.corporationNumber,
      corporationName: state.company.name,
      accPeriod: state.company.accPeriod,
      userId: state.user.sub,
      userName: state.user.userName,
      authority: state.authority,
      mode: state.analysis.mode
    }
  }, shallowEqual)

  // 権限を持っていない場合は画面表示しない
  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.DataMapping.UserClassificationItem_Set
  ) {
    return <Forbidden />
  }

  const props = {
    ...state,
    pageTitle: titleName.classificationInfo + titleName.common,
    bucketName: createBucketName(process.env.REACT_APP_ENV, state.corporationNumber),
    navigate,
    showModal,
    modalExtra,
    showToast
  }

  return (
    <UserClassificationInfoProvider>
      <UserClassificationInfoContent {...props} />
    </UserClassificationInfoProvider>
  )
}

export default UserClassificationInfo
